import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Star, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { api } from "@/lib/site";
import { useAuth } from "@/lib/auth";

export default function Pricing() {
  const [data, setData] = useState(null);
  const [loadingKey, setLoadingKey] = useState(null);
  const navigate = useNavigate();
  const { user, ready } = useAuth();

  // Creator-only page: fans get redirected
  useEffect(() => {
    if (!ready) return;
    const role = localStorage.getItem("ff_role");
    if (user?.type === "fan" || (!user && role === "fan")) {
      toast("Pricing plans are for creators — here's how you use Fan Focus as a fan.");
      navigate("/for-fans", { replace: true });
    }
  }, [ready, user, navigate]);

  useEffect(() => {
    api.get("/plans").then((r) => setData(r.data)).catch(() => toast.error("Could not load plans"));
  }, []);

  const checkout = async (lookup_key) => {
    try {
      setLoadingKey(lookup_key);
      const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Unable to start checkout. Please try again.");
      setLoadingKey(null);
    }
  };

  const plans = data?.plans || [];
  const founding = data?.founding;

  return (
    <div className="pt-32">
      <section className="max-w-7xl mx-auto px-6 text-center" data-testid="pricing-hero">
        <Reveal>
          <p className="overline text-gold mb-4">Membership</p>
          <h1 className="font-display text-6xl md:text-8xl uppercase text-white">Choose Your Plan</h1>
          <p className="mt-5 text-white/60 text-lg max-w-2xl mx-auto">Flexible tiers built to match where you are — and where you're going. Cancel anytime.</p>
        </Reveal>
      </section>

      {/* Founding 10 promo */}
      {founding && (
        <section className="max-w-7xl mx-auto px-6 mt-14">
          <Reveal>
            <div className="relative overflow-hidden border border-gold/40 bg-gradient-to-br from-[#171203] to-[#0b0b0b] p-8 md:p-10 flex flex-col md:flex-row md:items-center justify-between gap-6" data-testid="founding-banner">
              <div>
                <p className="overline text-gold mb-2">{founding.subtitle}</p>
                <h2 className="font-display text-4xl md:text-5xl uppercase text-white">{founding.title}</h2>
                <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2">
                  {founding.perks.map((p, i) => (
                    <span key={i} className="flex items-center gap-2 text-white/80 text-sm"><Check size={16} className="text-gold" />{p}</span>
                  ))}
                </div>
              </div>
              <div className="text-center md:text-right shrink-0">
                <p className="text-white/40 line-through font-cond text-lg">${founding.regular_price}/mo</p>
                <p className="font-display text-6xl text-gold leading-none">${founding.price}<span className="text-2xl text-white/60">/mo</span></p>
                <button onClick={() => checkout(founding.lookup_key)} disabled={loadingKey} data-testid="founding-checkout" className="mt-4 inline-flex items-center gap-2 bg-gold text-black px-8 py-3.5 overline hover:bg-[#F5D061] transition-colors duration-300 disabled:opacity-60">
                  {loadingKey === founding.lookup_key ? <Loader2 size={16} className="animate-spin" /> : <>Claim Spot <ArrowRight size={15} /></>}
                </button>
                <p className="mt-2 text-white/40 text-xs uppercase tracking-widest">First {founding.seats} creators only</p>
              </div>
            </div>
          </Reveal>
        </section>
      )}

      {/* Plans */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-5 items-stretch">
          {plans.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.08} className="h-full">
              <div
                data-testid={`plan-${p.id}`}
                className={`relative flex flex-col h-full border p-8 bg-[#0b0b0b] transition-[transform,border-color] duration-300 hover:-translate-y-1 ${
                  p.popular ? "border-gold shadow-[0_0_30px_rgba(212,175,55,0.12)]" : "border-white/10 hover:border-gold/40"
                }`}
              >
                {p.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold text-black text-[10px] font-semibold uppercase tracking-[0.2em] px-3 py-1 flex items-center gap-1">
                    <Star size={11} fill="black" /> Most Popular
                  </span>
                )}
                <p className="overline text-gold mb-3">{p.tagline}</p>
                <h3 className="font-display text-3xl uppercase text-white">{p.name}</h3>
                <div className="mt-4 mb-4 min-h-[64px]">
                  {p.price !== null ? (
                    <p className="font-display text-5xl text-white leading-none">${p.price}<span className="text-lg text-white/50">/mo</span></p>
                  ) : (
                    <p className="font-display text-4xl text-gold leading-none">Rev Share</p>
                  )}
                </div>
                <p className="text-white/50 text-sm mb-6 min-h-[40px]">{p.blurb}</p>
                <ul className="space-y-3 mb-8 flex-1">
                  {p.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-2.5 text-white/75 text-sm"><Check size={16} className="text-gold shrink-0 mt-0.5" />{f}</li>
                  ))}
                </ul>
                {p.checkout ? (
                  <button
                    onClick={() => checkout(p.lookup_key)}
                    disabled={loadingKey}
                    data-testid={`checkout-${p.id}`}
                    className={`w-full py-3.5 overline transition-colors duration-300 disabled:opacity-60 ${
                      p.popular ? "bg-gold text-black hover:bg-[#F5D061]" : "border border-white/25 text-white hover:border-gold hover:text-gold"
                    }`}
                  >
                    {loadingKey === p.lookup_key ? <Loader2 size={16} className="animate-spin mx-auto" /> : "Subscribe"}
                  </button>
                ) : (
                  <button
                    onClick={() => navigate("/contact#apply")}
                    data-testid={`apply-${p.id}`}
                    className="w-full py-3.5 overline border border-gold text-gold hover:bg-gold hover:text-black transition-colors duration-300"
                  >
                    Apply Now
                  </button>
                )}
              </div>
            </Reveal>
          ))}
        </div>
        <p className="mt-8 text-center text-white/40 text-sm">Secure checkout by Stripe · Test card 4242 4242 4242 4242 · Cancel anytime</p>
      </section>
    </div>
  );
}
